/** Shared year-mode / prefix helpers for document numbering (offers + invoices). */

export type DocumentYearMode = 'yy' | 'year' | 'none';
export type DocumentNumberDelimiter = '-' | '/' | '.';

export const DOCUMENT_SEQ_DIGITS_MIN = 1;
export const DOCUMENT_SEQ_DIGITS_MAX = 8;

export const DOCUMENT_NUMBER_DELIMITERS = ['-', '/', '.'] as const;
export const DEFAULT_DOCUMENT_NUMBER_DELIMITER: DocumentNumberDelimiter = '-';

export const DEFAULT_OFFERING_NUMBER_PREFIX = 'ANG';
export const DEFAULT_OFFERING_EXPORT_PREFIX = 'AN';
export const DEFAULT_INVOICE_PREFIX_RECHNUNG = 'RE';
export const DEFAULT_INVOICE_PREFIX_ABSCHLAG = 'AR';
export const DEFAULT_INVOICE_PREFIX_SCHLUSS = 'SR';
export const DEFAULT_INVOICE_PREFIX_STORNO = 'ST';
export const DEFAULT_INVOICE_PREFIX_GUTSCHRIFT = 'GS';

export const INVOICE_FAMILY_LABELS = [
  'Rechnung',
  'Abschlagsrechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
] as const;

export type InvoiceFamilyLabel = (typeof INVOICE_FAMILY_LABELS)[number];

export function isInvoiceFamilyDocumentType(label: string): boolean {
  return (INVOICE_FAMILY_LABELS as readonly string[]).includes(String(label ?? '').trim());
}

export function normalizeDocumentYearMode(raw: unknown): DocumentYearMode {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'year' || v === 'none' || v === 'yy') return v;
  return 'yy';
}

export function normalizeDocumentSeqDigits(raw: unknown, fallback = 5): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return fallback;
  if (n < DOCUMENT_SEQ_DIGITS_MIN || n > DOCUMENT_SEQ_DIGITS_MAX) return fallback;
  return n;
}

export function normalizeDocumentDelimiter(raw: unknown): DocumentNumberDelimiter {
  const v = String(raw ?? '').trim();
  if (v === '-' || v === '/' || v === '.') return v;
  return DEFAULT_DOCUMENT_NUMBER_DELIMITER;
}

/** Prefer the delimiter already present in an issued number; else the configured fallback. */
export function detectDocumentNumberDelimiter(
  value: string,
  fallback?: string,
): DocumentNumberDelimiter {
  const trimmed = String(value ?? '');
  for (const d of DOCUMENT_NUMBER_DELIMITERS) {
    if (trimmed.includes(d)) return d;
  }
  return normalizeDocumentDelimiter(fallback);
}

export function normalizeDocumentIncludeMonth(raw: unknown): boolean {
  return raw === true || raw === 'true';
}

export function normalizeDocumentPrefix(
  raw: unknown,
  fallback: string,
  options?: { allowEmpty?: boolean },
): string {
  if (raw === undefined || raw === null) return fallback;
  const v = String(raw).trim();
  if (v.length > 16 || /[{}]/.test(v)) return fallback;
  if (!v) return options?.allowEmpty ? '' : fallback;
  return v;
}

/** Counter / sequence key: YY, YYYY, or "0" when year is omitted from the format. */
export function documentYearKey(mode: DocumentYearMode, date: Date = new Date()): string {
  if (mode === 'none') return '0';
  if (mode === 'year') return String(date.getFullYear());
  return String(date.getFullYear()).slice(-2);
}

/** Calendar month as MM (01–12). Same local clock as documentYearKey. */
export function documentMonthKey(date: Date = new Date()): string {
  return String(date.getMonth() + 1).padStart(2, '0');
}

/**
 * Sequence period key. Year only: 26 / 2026 / 0.
 * With month: 26M09 (delimiter-independent so changing / ↔ - does not fork the counter).
 */
export function documentPeriodKey(
  yearMode: DocumentYearMode,
  includeMonth: boolean,
  date: Date = new Date(),
): string {
  const yearKey = documentYearKey(yearMode, date);
  if (!includeMonth) return yearKey;
  return `${yearKey}M${documentMonthKey(date)}`;
}

export function formatDocumentSerial(serial: number, digits: number): string {
  const s = Math.max(1, Math.floor(Number(serial)) || 1);
  const d = normalizeDocumentSeqDigits(digits, 5);
  if (d <= 1) return String(s);
  return String(s).padStart(d, '0');
}

export type DocumentNumberBuildParts = {
  prefix: string;
  yearMode: DocumentYearMode;
  yearKey: string;
  serial: number;
  seqDigits: number;
  includeMonth?: boolean;
  monthKey?: string;
  delimiter?: string;
};

/** Join present segments: [prefix] [year] [month] [serial]. */
export function buildDocumentNumber(parts: DocumentNumberBuildParts): string {
  const prefix = normalizeDocumentPrefix(parts.prefix, 'DOC', { allowEmpty: true });
  const delimiter = normalizeDocumentDelimiter(parts.delimiter);
  const serialPart = formatDocumentSerial(parts.serial, parts.seqDigits);
  const segments: string[] = [];
  if (prefix) segments.push(prefix);
  if (parts.yearMode !== 'none') segments.push(parts.yearKey);
  if (parts.includeMonth && parts.monthKey) segments.push(parts.monthKey);
  segments.push(serialPart);
  return segments.join(delimiter);
}

export function escapeRegExp(value: string): string {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type DocumentSerialParseOptions = {
  includeMonth?: boolean;
  monthKey?: string;
  delimiter?: string;
};

/** Match serial from a document number given prefixes + year mode. */
export function parseDocumentSerial(
  num: string,
  prefixes: string[],
  yearMode: DocumentYearMode,
  yearKey: string,
  options?: DocumentSerialParseOptions,
): number | null {
  const trimmed = String(num ?? '').trim();
  if (!trimmed) return null;
  const delimiter = normalizeDocumentDelimiter(options?.delimiter);
  const d = escapeRegExp(delimiter);
  const nonEmptyPrefs = prefixes.map((p) => String(p ?? '').trim()).filter(Boolean);
  const prefixPart = nonEmptyPrefs.length
    ? `(?:${nonEmptyPrefs.map(escapeRegExp).join('|')})${d}`
    : '';
  if (!prefixPart && yearMode === 'none' && !options?.includeMonth) return null;

  let mid = '';
  if (yearMode !== 'none') {
    mid += escapeRegExp(yearKey);
    if (options?.includeMonth && options.monthKey) {
      mid += `${d}${escapeRegExp(options.monthKey)}`;
    }
    mid += d;
  } else if (options?.includeMonth && options.monthKey) {
    mid += `${escapeRegExp(options.monthKey)}${d}`;
  }

  const re = new RegExp(`^${prefixPart}${mid}(\\d+)(?:${d}\\d+)?$`);
  const m = trimmed.match(re);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
}

export type DocumentNumberFormatSettings = {
  offering_number_prefix: string;
  offering_number_year_mode: DocumentYearMode;
  offering_number_seq_digits: number;
  offering_number_include_month: boolean;
  offering_number_delimiter: DocumentNumberDelimiter;
  offering_export_prefix: string;
  offering_export_use_version: boolean;
  invoice_number_year_mode: DocumentYearMode;
  invoice_number_seq_digits: number;
  invoice_number_include_month: boolean;
  invoice_number_delimiter: DocumentNumberDelimiter;
  invoice_prefix_rechnung: string;
  invoice_prefix_abschlag: string;
  invoice_prefix_schluss: string;
  invoice_prefix_storno: string;
  invoice_prefix_gutschrift: string;
};

export function defaultDocumentNumberFormatSettings(): DocumentNumberFormatSettings {
  return {
    offering_number_prefix: DEFAULT_OFFERING_NUMBER_PREFIX,
    offering_number_year_mode: 'yy',
    offering_number_seq_digits: 5,
    offering_number_include_month: false,
    offering_number_delimiter: DEFAULT_DOCUMENT_NUMBER_DELIMITER,
    offering_export_prefix: DEFAULT_OFFERING_EXPORT_PREFIX,
    offering_export_use_version: true,
    invoice_number_year_mode: 'yy',
    invoice_number_seq_digits: 5,
    invoice_number_include_month: false,
    invoice_number_delimiter: DEFAULT_DOCUMENT_NUMBER_DELIMITER,
    invoice_prefix_rechnung: DEFAULT_INVOICE_PREFIX_RECHNUNG,
    invoice_prefix_abschlag: DEFAULT_INVOICE_PREFIX_ABSCHLAG,
    invoice_prefix_schluss: DEFAULT_INVOICE_PREFIX_SCHLUSS,
    invoice_prefix_storno: DEFAULT_INVOICE_PREFIX_STORNO,
    invoice_prefix_gutschrift: DEFAULT_INVOICE_PREFIX_GUTSCHRIFT,
  };
}

function mapStoredPrefix(raw: unknown, fallback: string): string {
  return normalizeDocumentPrefix(raw, fallback, { allowEmpty: true });
}

export function mapDocumentNumberFormatSettings(row: Record<string, unknown> | null | undefined): DocumentNumberFormatSettings {
  const d = defaultDocumentNumberFormatSettings();
  if (!row) return d;
  return {
    offering_number_prefix: normalizeDocumentPrefix(row.offering_number_prefix, d.offering_number_prefix),
    offering_number_year_mode: normalizeDocumentYearMode(row.offering_number_year_mode),
    offering_number_seq_digits: normalizeDocumentSeqDigits(row.offering_number_seq_digits, d.offering_number_seq_digits),
    offering_number_include_month: normalizeDocumentIncludeMonth(row.offering_number_include_month),
    offering_number_delimiter: normalizeDocumentDelimiter(row.offering_number_delimiter),
    offering_export_prefix: normalizeDocumentPrefix(row.offering_export_prefix, d.offering_export_prefix),
    offering_export_use_version: row.offering_export_use_version === false ? false : true,
    invoice_number_year_mode: normalizeDocumentYearMode(row.invoice_number_year_mode),
    invoice_number_seq_digits: normalizeDocumentSeqDigits(row.invoice_number_seq_digits, d.invoice_number_seq_digits),
    invoice_number_include_month: normalizeDocumentIncludeMonth(row.invoice_number_include_month),
    invoice_number_delimiter: normalizeDocumentDelimiter(row.invoice_number_delimiter),
    invoice_prefix_rechnung: mapStoredPrefix(row.invoice_prefix_rechnung, d.invoice_prefix_rechnung),
    invoice_prefix_abschlag: mapStoredPrefix(row.invoice_prefix_abschlag, d.invoice_prefix_abschlag),
    invoice_prefix_schluss: mapStoredPrefix(row.invoice_prefix_schluss, d.invoice_prefix_schluss),
    invoice_prefix_storno: mapStoredPrefix(row.invoice_prefix_storno, d.invoice_prefix_storno),
    invoice_prefix_gutschrift: mapStoredPrefix(row.invoice_prefix_gutschrift, d.invoice_prefix_gutschrift),
  };
}

export function invoicePrefixForLabel(
  settings: DocumentNumberFormatSettings,
  documentTypeLabel: string,
): string {
  switch (String(documentTypeLabel ?? '').trim()) {
    case 'Abschlagsrechnung':
      return settings.invoice_prefix_abschlag;
    case 'Schlussrechnung':
      return settings.invoice_prefix_schluss;
    case 'Stornorechnung':
      return settings.invoice_prefix_storno;
    case 'Gutschrift':
      return settings.invoice_prefix_gutschrift;
    case 'Rechnung':
    default:
      return settings.invoice_prefix_rechnung;
  }
}

export function invoiceFamilyPrefixes(settings: DocumentNumberFormatSettings): string[] {
  return [
    settings.invoice_prefix_rechnung,
    settings.invoice_prefix_abschlag,
    settings.invoice_prefix_schluss,
    settings.invoice_prefix_storno,
    settings.invoice_prefix_gutschrift,
  ];
}

export function invoiceFamilyPrefixesAreUniform(settings: DocumentNumberFormatSettings): boolean {
  const emptyFlags = invoiceFamilyPrefixes(settings).map((p) => !String(p ?? '').trim());
  return emptyFlags.every(Boolean) || emptyFlags.every((empty) => !empty);
}

export function invoicePeriodKey(settings: DocumentNumberFormatSettings, date: Date = new Date()): string {
  return documentPeriodKey(settings.invoice_number_year_mode, settings.invoice_number_include_month, date);
}

export function offeringPeriodKey(settings: DocumentNumberFormatSettings, date: Date = new Date()): string {
  return documentPeriodKey(settings.offering_number_year_mode, settings.offering_number_include_month, date);
}

export function invoiceSerialParseOptions(
  settings: DocumentNumberFormatSettings,
  date: Date = new Date(),
): DocumentSerialParseOptions {
  return {
    includeMonth: settings.invoice_number_include_month,
    monthKey: settings.invoice_number_include_month ? documentMonthKey(date) : undefined,
    delimiter: settings.invoice_number_delimiter,
  };
}

export function offeringSerialParseOptions(
  settings: DocumentNumberFormatSettings,
  date: Date = new Date(),
): DocumentSerialParseOptions {
  return {
    includeMonth: settings.offering_number_include_month,
    monthKey: settings.offering_number_include_month ? documentMonthKey(date) : undefined,
    delimiter: settings.offering_number_delimiter,
  };
}

export function invoiceNumberBuildFields(
  settings: DocumentNumberFormatSettings,
  date: Date = new Date(),
): Pick<DocumentNumberBuildParts, 'yearMode' | 'yearKey' | 'includeMonth' | 'monthKey' | 'delimiter' | 'seqDigits'> {
  return {
    yearMode: settings.invoice_number_year_mode,
    yearKey: documentYearKey(settings.invoice_number_year_mode, date),
    includeMonth: settings.invoice_number_include_month,
    monthKey: documentMonthKey(date),
    delimiter: settings.invoice_number_delimiter,
    seqDigits: settings.invoice_number_seq_digits,
  };
}

export function offeringNumberBuildFields(
  settings: DocumentNumberFormatSettings,
  date: Date = new Date(),
): Pick<DocumentNumberBuildParts, 'yearMode' | 'yearKey' | 'includeMonth' | 'monthKey' | 'delimiter' | 'seqDigits'> {
  return {
    yearMode: settings.offering_number_year_mode,
    yearKey: documentYearKey(settings.offering_number_year_mode, date),
    includeMonth: settings.offering_number_include_month,
    monthKey: documentMonthKey(date),
    delimiter: settings.offering_number_delimiter,
    seqDigits: settings.offering_number_seq_digits,
  };
}

function prefixTooLongOrBraces(raw: string): boolean {
  const v = String(raw ?? '').trim();
  return v.length > 16 || /[{}]/.test(v);
}

/** Save-time guards: month implies year; empty invoice prefixes are all-or-nothing; empty prefix needs year. */
export function validateDocumentNumberFormatSettings(settings: DocumentNumberFormatSettings): string | null {
  if (settings.invoice_number_include_month && settings.invoice_number_year_mode === 'none') {
    return 'Monat in der Rechnungsnummer setzt ein Jahresformat voraus.';
  }
  if (settings.offering_number_include_month && settings.offering_number_year_mode === 'none') {
    return 'Monat in der Vorgangsnummer setzt ein Jahresformat voraus.';
  }
  for (const [value, label] of [
    [settings.offering_number_prefix, 'Präfix der Vorgangsnummer'],
    [settings.offering_export_prefix, 'Präfix der Angebotsnummer'],
    [settings.invoice_prefix_rechnung, 'Präfix Rechnung'],
    [settings.invoice_prefix_abschlag, 'Präfix Abschlagsrechnung'],
    [settings.invoice_prefix_schluss, 'Präfix Schlussrechnung'],
    [settings.invoice_prefix_storno, 'Präfix Stornorechnung'],
    [settings.invoice_prefix_gutschrift, 'Präfix Gutschrift'],
  ] as const) {
    if (prefixTooLongOrBraces(value)) {
      return `${label} darf höchstens 16 Zeichen haben und keine { }.`;
    }
  }
  if (!invoiceFamilyPrefixesAreUniform(settings)) {
    return 'Leeres Präfix gilt für alle Rechnungstypen gemeinsam.';
  }
  if (!settings.invoice_prefix_rechnung.trim() && settings.invoice_number_year_mode === 'none') {
    return 'Ohne Präfix muss die Rechnungsnummer ein Jahr enthalten.';
  }
  if (!settings.offering_number_prefix.trim()) {
    return 'Die Vorgangsnummer braucht ein Präfix.';
  }
  if (!settings.offering_export_prefix.trim()) {
    return 'Die Angebotsnummer braucht ein Präfix.';
  }
  return null;
}
